import { useEffect } from 'react';
import { DFPManager } from '@dsch/react-dfp';

import { adViewApi } from '../../../api/adViewApi';
import { fireToast } from '../../../helpers/Toasts';
import { HomePageResponse } from '../../../api/types/adviewApiTypes';
import {
  AdSummary,
  DataTargetingProps,
  LifestyleLinks,
  PromoSlot,
} from '../../../types';
import { useUserContext } from '../../../contexts/UserContext';
import { pushToDataLayer } from '../../../services/gtmService';
import { PAGE } from '../../../helpers/pages';
import { logger } from '@dist-marketplace-frontend/services';
import { VERTICAL } from '../../../helpers/verticals';
import { useVerticalContext } from '../../../contexts/VerticalContext';
import { useQuery } from '@tanstack/react-query';

export type Section = {
  id?: number;
  name: string;
  displayName: string;
  link?: string;
  query?: { [key: string]: string };
};

export type HomePageData = {
  sectionsTitle: string;
  sections: Section[];
  recentAds: AdSummary[];
  recentAdsUrl: string | null;
  featuredAds: PromoSlot | null;
  lifestyleLinks?: LifestyleLinks[];
};

type UseHomePageProps = {
  init: { homePageData: HomePageData; vertical: VERTICAL };
  dataTargetingProps: DataTargetingProps;
  lifestyleLinks?: LifestyleLinks[];
};

const formatVertical = (vertical: VERTICAL) => {
  return vertical.charAt(0).toUpperCase() + vertical.substring(1);
};

const getDFPSettings = (vertical: VERTICAL) => ({
  site: 'donedealclassifieds',
  vertical: [formatVertical(vertical)],
});

const updateDFP = (
  vertical: VERTICAL,
  dataTargetingProps: DataTargetingProps,
) => {
  DFPManager.setTargetingArguments({
    ...getDFPSettings(vertical),
    ...dataTargetingProps?.dfp,
  });
};

const H1 = {
  /**
   * @warning Used by Pingdom to check if the site is up.
   * If you change this string, please inform SRE.
   */
  MOTOR: 'Browse by category',
  MARKETPLACE: 'Buy & Sell Locally in Ireland',
  FARMING: 'Buy & Sell Farm Machinery & Tractors',
};

export const getSectionsTitle = (vertical: VERTICAL): string => {
  switch (vertical) {
    case VERTICAL.MOTOR:
      return H1.MOTOR;
    case VERTICAL.MARKETPLACE:
      return H1.MARKETPLACE;
    case VERTICAL.FARMING:
      return H1.FARMING;
    default:
      return H1.MARKETPLACE;
  }
};

const mapHomePageResponse = (
  vertical: VERTICAL,
  response: HomePageResponse,
): HomePageData => ({
  sectionsTitle: getSectionsTitle(vertical),
  sections: response.sections,
  recentAds: response.recentAds,
  recentAdsUrl: response.recentAdsUrl,
  featuredAds: response.featuredDealer,
  lifestyleLinks: response.lifestyleLinks || [],
});

const useHomePage = (props: UseHomePageProps) => {
  const { init, dataTargetingProps } = props;
  const { vertical } = useVerticalContext();
  const { ddRecommendedUser } = useUserContext();

  useEffect(() => {
    updateDFP(vertical, dataTargetingProps);
    // NB: The dataTargetingProps never changes as it comes from the server side
  }, [vertical, dataTargetingProps]);

  const query = useQuery({
    queryKey: ['homePageData', vertical],
    queryFn: () => updateHomePageData(vertical, ddRecommendedUser),
    // The initial data exist only for the vertical that was used during server side rendering
    initialData: init.vertical === vertical ? init.homePageData : undefined,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return { homePageData: query.data, ...query };
};

const updateHomePageData = async (
  vertical: VERTICAL,
  ddRecommendedUser: string | number | undefined,
) => {
  try {
    const { data } = await adViewApi.getHomePageData(
      vertical,
      ddRecommendedUser
        ? {
            dd_recommendation_user: ddRecommendedUser.toString(),
          }
        : undefined,
    );
    pushToDataLayer(data.dataTargetingProps.gtm);

    return mapHomePageResponse(vertical, data);
  } catch (error) {
    logger.error({
      error: error,
      message: 'Error handling updateHomePageData',
      tags: [PAGE.HOME],
    });
    fireToast({
      type: 'ERROR',
      text: 'Oops! Something went wrong, please try again later',
    });
    throw error;
  }
};

export { useHomePage };
